/**
 * Home: the front of the site, whoever is looking at it.
 *
 * One composition, in three states that differ by one panel. The framed hero
 * with the insignia cropped by its own right edge, the pitch, and the three
 * facts under it are the site's front page and stay the front page after
 * signing in — what changes is that the sign-in form is replaced by the
 * player's pilot card, or, when the control plane cannot be reached, by the
 * one line that says so.
 *
 * That third state is why the panel is a parameter rather than a session. This
 * page is served from S3 and holds its own copy of everything on it, so it is
 * up whenever the bucket is; the API being down costs signing in and nothing
 * else, and the page has to keep saying what the site is either way.
 *
 * The signed-in home used to be a different page: an account card, and the
 * match list beside it. The matches moved to a screen of their own, which is
 * where starting one lives too.
 *
 * The prose here is not placeholder any more; a human wrote it, so leave it
 * alone unless they ask. The sign-in form is not written here at all — it is
 * the site's one form, from signin-form.ts.
 */

import { alphaStamp } from "../alpha-stamp";
import type { Session } from "../api";
import { footer } from "../chrome";
import { el } from "../dom";
import { hostCycle } from "../host-cycle";
import { insignia } from "../insignia";
import { outagePanel } from "../outage-panel";
import { signInForm } from "../signin-form";
import { notReadyNote, soonStamp } from "../soon-stamp";
import { dailyCard } from "./daily";
import { pilotCard } from "./pilot";

/**
 * Who the hero's panel is for.
 *
 * A union rather than a session plus flags, because the three cases exclude
 * each other and two of them carry something the others have no use for: a
 * signed-out reader can be shown the error a failed OAuth callback came back
 * with, and there is no form to put that above otherwise.
 */
export type HomePanel =
  | { readonly kind: "signedIn"; readonly session: Session }
  | { readonly kind: "signedOut"; readonly error?: string }
  /** The control plane did not answer. `retry` reads the session again. */
  | {
      readonly kind: "unreachable";
      readonly message: string;
      readonly retry: () => void;
    };

/** The home screen. Everything but the panel is the same in all three states. */
export function homeScreen(state: HomePanel): Node[] {
  // Signed out, the site's one sign-in form (signin-form.ts) — the same one the
  // masthead's Login button and the camo editor put up in a dialog. Nothing
  // about the request differs here, so nothing about the form does either: the
  // hero supplies the panel it sits in, and that is all it supplies.
  const panel =
    state.kind === "signedIn"
      ? pilotCard(state.session)
      : state.kind === "signedOut"
        ? signInForm({ error: state.error }).form
        : outagePanel(state.message, state.retry);

  return [
    el("section", { className: "hero" }, [
      // Absolutely positioned and cropped by the hero's own edges, so it is
      // read before the words and never sits behind them at full strength.
      el("div", { className: "hero-mark" }, [insignia("hero-insignia")]),
      // Stamped across the corner the insignia is cropped by, over the artwork
      // rather than beside it: what the site is, said once where a first visit
      // is looking. Above the mark and below nothing — the copy is a column
      // down the left and the stamp never reaches it.
      alphaStamp("hero"),
      el("div", { className: "hero-copy" }, [
        // The headline the site is fronted by. The last word carries the
        // emphasis, and the markup is built around that.
        el("h1", {}, [
          "Reactor, online. Sensors, online. Weapons, ",
          el("span", { className: "hero-em", textContent: "online." }),
        ]),
        // The last word is not a word, it is a rotation: see host-cycle.ts for
        // why the sentence must not end on one host's name, and why the full
        // stop is in there with the names rather than out here.
        el("p", { className: "hero-lead" }, [
          "Meet your mutual on the battlefield, put a gauss slug through their cockpit, and brag about it on ",
          hostCycle(),
        ]),
        panel,
      ]),
    ]),
    // Under the hero and above the facts, in all three states: what is on
    // today is the same question whether or not anyone is signed in, and a
    // stranger who has never heard of this is exactly who it is for. Dropped
    // entirely when nothing is scheduled, rather than shown empty.
    ...maybe(dailyCard()),
    factsStrip(),
    footer(),
  ];
}

/** A node, or nothing at all, as a list to spread. */
function maybe(node: Node | null): Node[] {
  return node ? [node] : [];
}

/**
 * The strip under the hero, and which of it is a promise rather than a
 * description.
 *
 * Two of the three facts are written in the present tense about things the
 * site cannot do yet: there is no force designer and no AAR to post, and
 * nothing issues a commendation. Both are stamped, because a front page that
 * says all three in the same voice is telling a stranger something untrue, and
 * they find out from a menu item that does not click.
 *
 * The stamp goes over the card, across the middle of it, and nothing under it
 * is dimmed to make room. Dimming is how this site's readable text has stopped
 * being readable before — --muted under an opacity, which is what
 * contrast.test.mjs exists to catch — and the paragraph on this card is
 * --muted already. The mark is translucent instead, the way the hero's is over
 * the insignia.
 */
function factsStrip(): HTMLElement {
  const fact = (
    title: string,
    body: (Node | string)[],
    soon = false,
  ): HTMLElement =>
    el("section", { className: soon ? "fact fact-soon" : "fact" }, [
      el("h2", {}, [
        title,
        // What a screen reader gets in place of the angle and the colour, in
        // the reading order the title is in. The same words the bar's unbuilt
        // destinations carry, for the same reason.
        ...(soon ? [notReadyNote()] : []),
      ]),
      el("p", {}, body),
      ...(soon ? [soonStamp("fact")] : []),
    ]);

  return el("div", { className: "facts" }, [
    fact("Built on MegaMek", [
      "Play BattleTech with an online MegaMek client, patched to integrate with the Atmosphere.",
    ]),
    fact(
      "Atmospheric Integration",
      [
        "Design your forces, play against your mutuals, and post AARs - all of it stays stored on your Atmosphere account.",
      ],
      true,
    ),

    fact(
      "Achievements",
      [
        "Earn commendations by playing matches. They're stored in your account, and can be displayed by other sites.",
      ],
      true,
    ),
  ]);
}
